/**
 * Base API Service Configuration - Updated with Store Management
 * Handles authentication, company context, store context, and common request/response logic
 */

import axios from 'axios';

// Get configuration from environment variables
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://localhost:7298';
const IMAGE_BASE_URL = import.meta.env.VITE_IMAGE_BASE_URL || 'https://stashhouseimages.blob.core.windows.net';

/**
 * Create axios instance with default configuration
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true, // KAN-149: auth rides entirely on the HttpOnly access-token cookie the browser attaches automatically — the JWT is no longer in localStorage or the Authorization header
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

/**
 * Token management - Updated with Store Support
 */
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';
const COMPANY_KEY = 'current_company';
const STORE_KEY = 'current_store';
const SESSION_EXPIRES_KEY = 'session_expires_at';

export const tokenManager = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  setToken: (token) => localStorage.setItem(TOKEN_KEY, token),
  removeToken: () => localStorage.removeItem(TOKEN_KEY),
  
  getUser: () => {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  },
  setUser: (user) => localStorage.setItem(USER_KEY, JSON.stringify(user)),
  removeUser: () => localStorage.removeItem(USER_KEY),
  
  getCurrentCompany: () => {
    const company = localStorage.getItem(COMPANY_KEY);
    return company ? JSON.parse(company) : null;
  },
  setCurrentCompany: (company) => localStorage.setItem(COMPANY_KEY, JSON.stringify(company)),
  removeCurrentCompany: () => localStorage.removeItem(COMPANY_KEY),
  
  // Session expiry management
  getSessionExpiry: () => localStorage.getItem(SESSION_EXPIRES_KEY),
  setSessionExpiry: (expiresAt) => localStorage.setItem(SESSION_EXPIRES_KEY, expiresAt),

  // Store management methods
  getCurrentStore: () => {
    const store = localStorage.getItem(STORE_KEY);
    return store ? JSON.parse(store) : null;
  },
  setCurrentStore: (store) => localStorage.setItem(STORE_KEY, JSON.stringify(store)),
  removeCurrentStore: () => localStorage.removeItem(STORE_KEY),
  
  clearAll: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(COMPANY_KEY);
    localStorage.removeItem(STORE_KEY);
    localStorage.removeItem(SESSION_EXPIRES_KEY);
  }
};

/**
 * Track the API version from the first response we receive.
 * If a later response returns a different version, dispatch an event
 * so the UI can notify the user.
 */
let knownAppVersion = null;
let versionMismatchNotified = false;

function checkAppVersion(response) {
  const serverVersion = response.headers?.['x-app-version'];
  if (!serverVersion) return;

  if (!knownAppVersion) {
    knownAppVersion = serverVersion;
    return;
  }

  if (serverVersion !== knownAppVersion && !versionMismatchNotified) {
    versionMismatchNotified = true;
    window.dispatchEvent(new CustomEvent('app-version-update'));
  }
}

/**
 * Request interceptor - Add auth token, company headers, and store headers
 */
apiClient.interceptors.request.use(
  (config) => {
    // KAN-149: the JWT now travels only in the HttpOnly `access-token` cookie, which the
    // browser attaches automatically (withCredentials above). We no longer read it from
    // localStorage or set an Authorization header.

    // Add company header for multi-company support
    const currentCompany = tokenManager.getCurrentCompany();
    if (currentCompany?.id) {
      config.headers['X-Company-Id'] = currentCompany.id;
    }
    
    // NEW: Add store/client header for store-specific operations
    const currentStore = tokenManager.getCurrentStore();
    if (currentStore?.id) {
      config.headers['X-Client-Id'] = currentStore.id;
      config.headers['X-Store-Id'] = currentStore.id; // Alternative header name
    }
    
    // Add request timestamp for debugging
    config.metadata = { startTime: new Date() };
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

/**
 * Token refresh - extends session by 1 hour on API activity.
 * Throttled to run at most once every 10 minutes.
 */
let lastRefreshTime = 0;
const REFRESH_THROTTLE = 10 * 60 * 1000; // 10 minutes

const throttledTokenRefresh = () => {
  const now = Date.now();
  if (now - lastRefreshTime < REFRESH_THROTTLE) return;
  if (!tokenManager.getToken()) return;

  lastRefreshTime = now;

  // Fire-and-forget — don't block the original response
  apiClient.post('/storefront/refresh-token')
    .then((res) => {
      const { token, expiration } = res.data;
      if (token) tokenManager.setToken(token);
      if (expiration) tokenManager.setSessionExpiry(expiration);
    })
    .catch(() => {
      // Silently ignore — the existing token is still valid
    });
};

/**
 * Response interceptor - Handle common response scenarios
 */
apiClient.interceptors.response.use(
  (response) => {
    checkAppVersion(response);

    // Extend session on successful API activity (skip the refresh call itself)
    if (!response.config.url?.includes('/storefront/refresh-token')) {
      throttledTokenRefresh();
    }

    // Calculate request duration for debugging
    const duration = new Date() - response.config.metadata.startTime;
    console.log(`API Request completed in ${duration}ms:`, {
      method: response.config.method?.toUpperCase(),
      url: response.config.url,
      status: response.status,
      duration: `${duration}ms`
    });

    return response;
  },
  (error) => {
    // Check version even on error responses
    if (error.response) {
      checkAppVersion(error.response);
    }

    // Handle different error scenarios
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          // KAN-149: a session-bootstrap probe (storefront/session) opts out of the global
          // redirect via { skipAuthRedirect: true } — a 401 there just means "not logged in",
          // which the caller handles (a clean redirect to /login, no false "session expired").
          if (!error.config?.skipAuthRedirect) {
            // Unauthorized - clear tokens and redirect to login
            console.warn('Unauthorized access - clearing tokens');
            tokenManager.clearAll();

            // Only redirect if not already on login page
            if (!window.location.pathname.includes('/login')) {
              window.location.href = '/login?sessionExpired=true';
            }
          }
          break;
          
        case 403:
          // Forbidden - could be company or store access denied
          console.warn('Access forbidden:', data?.message || 'Insufficient permissions');
          
          // Check if it's a store access issue
          if (data?.message?.toLowerCase().includes('store') || 
              data?.message?.toLowerCase().includes('client')) {
            // Store access denied - might need to redirect to store selection
            console.warn('Store access denied - user may need to select different store');
          }
          break;
          
        case 404:
          console.warn('Resource not found:', error.config?.url);
          break;
          
        case 500:
          console.error('Server error:', data?.message || 'Internal server error');
          break;
          
        default:
          console.error(`API Error ${status}:`, data?.message || error.message);
      }
      
      // Transform error for consistent handling
      const apiError = {
        status,
        message: data?.message || data?.Message || error.message,
        errors: data?.errors || data?.Errors || [],
        details: data?.details || data?.Details || null,
        timestamp: new Date().toISOString(),
        // Include additional data from response for specific error handling
        data: data,
        remainingMinutes: data?.remainingMinutes,
        lockoutEnd: data?.lockoutEnd,
        statusCode: data?.statusCode || status
      };

      return Promise.reject(apiError);
    }
    
    // Network error or request setup error
    if (error.request) {
      console.error('Network error - no response received:', error.message);
      return Promise.reject({
        status: 0,
        message: 'Network error - please check your connection',
        isNetworkError: true,
        timestamp: new Date().toISOString()
      });
    }
    
    // Request configuration error
    console.error('Request setup error:', error.message);
    return Promise.reject({
      status: -1,
      message: 'Request configuration error',
      originalError: error.message,
      timestamp: new Date().toISOString()
    });
  }
);

/**
 * OData query builder helpers
 */
export const odataHelpers = {
  /**
   * Build OData $filter string
   * @param {object} filters - Filter conditions
   * @returns {string} OData filter string
   */
  buildFilter: (filters) => {
    if (!filters || typeof filters !== 'object') return '';
    
    const conditions = [];
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;
      
      if (Array.isArray(value)) {
        // Handle array values (IN operations)
        const inClause = value.map(v => typeof v === 'string' ? `'${v}'` : v).join(',');
        conditions.push(`${key} in (${inClause})`);
      } else if (typeof value === 'string') {
        // Handle string values
        if (value.includes('*')) {
          // Contains search
          const searchValue = value.replace(/\*/g, '');
          conditions.push(`contains(tolower(${key}), '${searchValue.toLowerCase()}')`);
        } else {
          conditions.push(`${key} eq '${value}'`);
        }
      } else if (typeof value === 'boolean') {
        conditions.push(`${key} eq ${value}`);
      } else {
        conditions.push(`${key} eq ${value}`);
      }
    });
    
    return conditions.join(' and ');
  },
  
  /**
   * Build OData $orderby string
   * @param {array} sorts - Sort configurations
   * @returns {string} OData orderby string
   */
  buildOrderBy: (sorts) => {
    if (!Array.isArray(sorts) || sorts.length === 0) return '';
    
    return sorts
      .map(sort => `${sort.field} ${sort.direction || 'asc'}`)
      .join(',');
  },
  
  /**
   * Build complete OData query string - UPDATED to handle $apply
   * @param {object} options - Query options
   * @returns {string} Complete query string
   */
  buildQueryString: (options = {}) => {
    const params = new URLSearchParams();
    
    // CRITICAL: Handle $apply parameter FIRST - required for groupby operations
    if (options.$apply) {
      params.append('$apply', options.$apply);
    }
    
    // Add other common OData parameters
    if (options.$filter) params.append('$filter', options.$filter);
    if (options.$orderby) params.append('$orderby', options.$orderby);
    if (options.$select) params.append('$select', options.$select);
    if (options.$expand) params.append('$expand', options.$expand);
    if (options.$top) params.append('$top', options.$top);
    if (options.$skip) params.append('$skip', options.$skip);
    if (options.$count !== undefined) params.append('$count', options.$count);
    
    // Add custom parameters
    Object.entries(options).forEach(([key, value]) => {
      if (!key.startsWith('$') && value !== undefined && value !== null) {
        params.append(key, value);
      }
    });

    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }
};

/**
 * Common API request methods
 */
export const apiService = {
  /**
   * GET request with OData support
   */
  get: async (endpoint, options = {}) => {
    const queryString = odataHelpers.buildQueryString(options);
    const response = await apiClient.get(`${endpoint}${queryString}`);
    return response.data;
  },
  
  /**
   * POST request
   */
  post: async (endpoint, data = null, config = {}) => {
    const response = await apiClient.post(endpoint, data, config);
    return response.data;
  },
  
  /**
   * PUT request
   */
  put: async (endpoint, data = null, config = {}) => {
    const response = await apiClient.put(endpoint, data, config);
    return response.data;
  },
  
  /**
   * PATCH request
   */
  patch: async (endpoint, data = null, config = {}) => {
    const response = await apiClient.patch(endpoint, data, config);
    return response.data;
  },
  
  /**
   * DELETE request
   */
  delete: async (endpoint, config = {}) => {
    const response = await apiClient.delete(endpoint, config);
    return response.data;
  },
  
  /**
   * Upload files (multipart/form-data)
   */
  upload: async (endpoint, formData, config = {}) => {
    const response = await apiClient.post(endpoint, formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
};

/**
 * Image URL helpers
 */
export const imageHelpers = {
  IMAGE_BASE_URL, // Export for use in other files
  
  /**
   * Get full image URL
   */
  getImageUrl: (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${IMAGE_BASE_URL}/${imagePath.replace(/^\//, '')}`;
  },
  
  /**
   * Get product image URL
   */
  getProductImageUrl: (product) => {
    if (product?.ProductImageURL) {
      return imageHelpers.getImageUrl('/productskuvariants/'+product.ProductImageURL);
    }
    if (product?.ImageURL) {
      return imageHelpers.getImageUrl(product.ImageURL);
    }
    return null;
  },
  
  /**
   * Get brand image URL
   */
  getBrandImageUrl: (brand) => {
    const filename = brand?.imageUrl;
    if (!filename) return [null];
    
    const brandId = brand?.id || brand?.ID;
    
    return [
      `${IMAGE_BASE_URL}/brands/${filename}`,              // Directory 1
      `${IMAGE_BASE_URL}/brands/${brandId}/${filename}`,   // Directory 2 (if brandId exists)
      null                             // Final fallback
    ].filter(Boolean); // Remove any null/undefined URLs
  },
};

/**
 * Error handling helpers
 */
export const errorHelpers = {
  /**
   * Extract user-friendly error message
   */
  getErrorMessage: (error) => {
    if (typeof error === 'string') return error;
    if (error?.message) return error.message;
    if (error?.Message) return error.Message;
    return 'An unexpected error occurred';
  },
  
  /**
   * Check if error is a network error
   */
  isNetworkError: (error) => {
    return error?.isNetworkError === true || error?.status === 0;
  },
  
  /**
   * Check if error is unauthorized
   */
  isUnauthorized: (error) => {
    return error?.status === 401;
  },
  
  /**
   * Check if error is forbidden
   */
  isForbidden: (error) => {
    return error?.status === 403;
  },
  
  /**
   * Check if error is not found
   */
  isNotFound: (error) => {
    return error?.status === 404;
  },
  
  /**
   * NEW: Check if error is store access related
   */
  isStoreAccessError: (error) => {
    const message = error?.message?.toLowerCase() || '';
    return error?.status === 403 && (
      message.includes('store') || 
      message.includes('client') ||
      message.includes('shop')
    );
  }
};

// Export the configured axios instance for direct use if needed
export { apiClient };

// Export default for easy importing
export default {
  apiService,
  tokenManager,
  odataHelpers,
  imageHelpers,
  errorHelpers,
  API_BASE_URL,
  IMAGE_BASE_URL
};